import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

/**
 * Sortable table helper.
 *
 * Usage:
 *   const { rows: sortedRows, sort, setSort } = useSortableTable(rows, columns);
 *   ...
 *   <SortableTh column="name" sort={sort} onSort={setSort}>Name</SortableTh>
 *
 * `columns` is a map `{ colKey: (row) => sortableValue }`. Each column key
 * you want to make sortable must have an entry — nulls sort last, strings
 * are case-insensitive, numbers/dates compare naturally.
 */
export function useSortableTable(rows, columns, initial = { key: null, dir: "asc" }) {
    const [sort, setSort] = useState(initial);

    const sorted = useMemo(() => {
        if (!sort.key || !columns[sort.key]) return rows;
        const getter = columns[sort.key];
        const dirMul = sort.dir === "desc" ? -1 : 1;
        const arr = [...rows];
        arr.sort((a, b) => {
            const av = getter(a);
            const bv = getter(b);
            // Nulls/undefineds always sink to the bottom regardless of dir
            // so the first click on an empty column doesn't produce a
            // useless "sea of blanks at the top".
            const aNil = av === null || av === undefined || av === "";
            const bNil = bv === null || bv === undefined || bv === "";
            if (aNil && bNil) return 0;
            if (aNil) return 1;
            if (bNil) return -1;
            if (typeof av === "number" && typeof bv === "number") return (av - bv) * dirMul;
            if (av instanceof Date && bv instanceof Date) return (av.getTime() - bv.getTime()) * dirMul;
            return String(av).toLowerCase().localeCompare(String(bv).toLowerCase()) * dirMul;
        });
        return arr;
    }, [rows, columns, sort]);

    function toggle(key) {
        setSort((s) => {
            if (s.key !== key) return { key, dir: "asc" };
            if (s.dir === "asc") return { key, dir: "desc" };
            return { key: null, dir: "asc" };
        });
    }

    return { rows: sorted, sort, setSort: toggle };
}

/**
 * Header cell that renders a caret + toggles sort on click. Falls back to
 * a plain `<th>` when `column` isn't supplied so tables can mix sortable
 * and non-sortable columns.
 */
export function SortableTh({ column, sort, onSort, className = "", align = "left", children, testid }) {
    const active = sort?.key === column;
    const alignCls = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
    if (!column) {
        return <th className={`${alignCls} px-4 py-3 ${className}`}>{children}</th>;
    }
    const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
        <th className={`${alignCls} px-4 py-3 ${className}`}>
            <button
                type="button"
                onClick={() => onSort(column)}
                className={`inline-flex items-center gap-1 uppercase tracking-wider font-semibold text-xs ${active ? "text-primary" : "text-inherit"} hover:text-primary transition-colors`}
                data-testid={testid || `sort-${column}`}
            >
                <span>{children}</span>
                <Icon className={`h-3 w-3 ${active ? "opacity-100" : "opacity-40"}`} />
            </button>
        </th>
    );
}
